import React, { useState, useEffect } from "react";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { useLocation, useNavigate } from "react-router-dom";
import ProgressPhotos from "../../components/common/ProgressPhotos";
import CommentBox from "../../components/common/CommentBox";
import CommentsSection from "../../components/common/CommentsSection";
import { 
  getAttachmentComments, 
  addAttachmentComment, 
  getWorkerProgress,
  editAttachmentComment,
  deleteAttachmentComment
} from "../../services/service";
import { Spin } from "antd";
import { IMAGE_BASE_URL } from "../../config/api";

const ViewSinglePhoto = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { photo } = location.state || {};
  
  console.log('Photo data received:', photo);

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!photo?.id) {
        console.log("No attachment ID available");
        return;
      }
      
      setLoading(true);
      try {
        // Fetch comments
        const commentsResponse = await getAttachmentComments(photo.id);
        if (commentsResponse.success) {
          setComments(commentsResponse.data);
        }

        // Fetch progress data using the progress_id
        if (photo.progress_id) {
          const progressResponse = await getWorkerProgress({ 
            progress_id: photo.progress_id 
          });
          console.log('Progress response:', progressResponse);
          if (progressResponse.success) {
            setProgressData(progressResponse.data[0]);
          }
        }
      } catch (err) {
        console.error("Error in fetchData:", err);
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [photo?.id, photo?.progress_id]);

  const handleCommentSubmit = async (comment) => {
    try {
      const response = await addAttachmentComment({
        comment,
        attachment_id: photo.id,
      });
      
      if (response.success) {
        const updatedComments = await getAttachmentComments(photo.id);
        if (updatedComments.success) {
          setComments(updatedComments.data);
        }
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  const handleReplySubmit = async (comment, parentId) => {
    try {
      const response = await addAttachmentComment({
        comment,
        attachment_id: photo.id,
        parent_id: parentId,
      });
      
      if (response.success) {
        const updatedComments = await getAttachmentComments(photo.id);
        if (updatedComments.success) {
          setComments(updatedComments.data);
        }
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };

  const refreshComments = async () => {
    if (!photo?.id) return;
    
    try {
      const updatedComments = await getAttachmentComments(photo.id);
      if (updatedComments.success) {
        setComments(updatedComments.data);
      }
    } catch (error) {
      console.error("Error refreshing comments:", error);
    }
  };

  const progressPhotos = [{ url: photo.url, commentCount: photo.commentCount }];

  return (
    <div className="w-full flex flex-col px-2 pb-12">
      <div className="w-full pb-2">
        <ListHeader
          mainHeading="Progress"
          btnShow={true}
          linkBreadCrum="/view-project"
          blinkBreadCrumText="Project"
          linkBreadCrum1="/view-project"
          linkBreadCrum2={`/project-details/${photo?.projectId}`}
          blinkBreadCrumText2="View detail"
          blinkBreadCrumText3="Photo"
          blinkBreadCrumText1="View project"
          searchShow={false}
          showLikeFilter={false}
          DropDown={false}
        />
      </div>

      {loading ? (
        <div className="w-full flex justify-center">
          <Spin />
        </div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="flex gap-5 mt-4">
          {/* Left Section - Image and Comments */}
          <div className="w-[60%] flex flex-col gap-4">
            <div className="bg-white rounded-[8px] p-4">
              <ProgressPhotos photos={progressPhotos} singlePhoto={true} />
            </div>

            <div className="bg-white rounded-[8px] p-4">
              <CommentBox worker={true} onSubmit={handleCommentSubmit} />
              <CommentsSection 
                comments={comments} 
                onReply={handleReplySubmit}
                onUpdate={refreshComments}
                editComment={editAttachmentComment}
                deleteComment={deleteAttachmentComment}
                commentType="attachment_comment"
              />
            </div>
          </div>

          {/* Right Section - Progress Details */}
          {progressData && (
            <div className="w-[40%] bg-white rounded-[8px] p-4">
              <div className="flex flex-col gap-6">
                {/* Worker Info */}
                <div 
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-[4px]"
                  onClick={() => navigate(`/worker-details/${progressData?.User?.id}`)}
                >
                  <img
                    src={progressData?.User?.image ? `${IMAGE_BASE_URL}${progressData.User.image}` : "https://pnghq.com/wp-content/uploads/2023/02/employee-photo-placeholder-png-1000.png"}
                    alt="Worker"
                    className="w-12 h-12 rounded-[4px] object-cover"
                  />
                  <div>
                    <h3 className="font-medium text-[16px] text-[#4B5563]">
                      {progressData?.User?.username}
                    </h3>
                    <p className="text-[13px] text-[#B7B7B7]">
                      Worker
                    </p>
                  </div>
                </div>

                {/* Progress Details */}
                <div className="flex flex-col gap-4">
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Progress Title</h4>
                    <p className="text-[14px] text-[#666]">{progressData?.title}</p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Project</h4>
                    <p className="text-[14px] text-[#666]">{progressData?.Project?.title}</p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Task</h4>
                    <p className="text-[14px] text-[#666]">{progressData?.TaskProgress?.title}</p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Location</h4>
                    <p className="text-[14px] text-[#666]">{progressData?.location}</p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Date</h4>
                    <p className="text-[14px] text-[#666]">
                      {new Date(progressData?.date).toLocaleDateString()}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Description</h4>
                    <p className="text-[14px] text-[#666]">{progressData?.description}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewSinglePhoto;
